/**
 * TasksPanelContent Component
 *
 * Tasks side panel: wires the task store/filters into the shared
 * `ArtifactListPanelContent` shell and supplies the task list.
 */

import { memo, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { useGeoMarkupStore, selectAllTasks } from '@/store';
import { useArtifactNavigation } from '@/hooks/useArtifactNavigation';
import { isOverlayPath } from '@/hooks/useBackgroundLocation';
import {
  selectTasksHasNextPage,
  selectTasksTotalCount,
  selectTasksHasLoaded,
} from '@/store/selectors/taskSelectors';
import {
  selectHasActiveTaskFiltersInMapPanel,
  selectTaskFilterKey,
} from '@/store/selectors/taskFilterSelectors';
import { selectCurrentWorkspaceId } from '@/store/selectors/authSelectors';
import { TasksPanelList } from '@/components/navigation/TasksPanel/components';
import { TaskFilters, TaskFilterPills } from '@/components/tasks/TaskFilters';
import { ArtifactListPanelContent } from './ArtifactListPanelContent';

interface TasksPanelContentProps {
  readonly onClose: () => void;
}

export const TasksPanelContent = memo(function TasksPanelContent({
  onClose,
}: TasksPanelContentProps) {
  const { t } = useTranslation();
  const allFilteredTasks = useGeoMarkupStore(useShallow(selectAllTasks));
  const totalCount = useGeoMarkupStore(selectTasksTotalCount);

  const search = useGeoMarkupStore((state) => state.taskFilters.search);
  const setSearch = useGeoMarkupStore((state) => state.taskFilters.setSearch);

  const isLoading = useGeoMarkupStore((state) => state.tasks.isLoading);
  const hasLoaded = useGeoMarkupStore(selectTasksHasLoaded);
  const tasksError = useGeoMarkupStore((state) => state.tasks.error);
  const fetchTasks = useGeoMarkupStore((state) => state.tasks.fetchTasks);
  const loadMoreTasks = useGeoMarkupStore((state) => state.tasks.loadMoreTasks);
  const hasNextPage = useGeoMarkupStore(selectTasksHasNextPage);
  const startCreateTaskFlow = useGeoMarkupStore(
    (state) => state.tasks.startCreateFlow
  );
  const { closeOverlay } = useArtifactNavigation();
  const location = useLocation();
  const isTaskFormOpen = useGeoMarkupStore((state) => state.tasks.formIsOpen);
  const isSelectingTaskLocation = useGeoMarkupStore(
    (state) => state.ui.isSelectingTaskLocation
  );
  const isAddTaskDisabled = isTaskFormOpen || isSelectingTaskLocation;

  const hasActiveFilters = useGeoMarkupStore(
    selectHasActiveTaskFiltersInMapPanel
  );
  const currentWorkspaceId = useGeoMarkupStore(selectCurrentWorkspaceId);
  const filterKey = useGeoMarkupStore(selectTaskFilterKey);

  useEffect(() => {
    if (currentWorkspaceId) {
      fetchTasks();
    }
  }, [currentWorkspaceId, filterKey, fetchTasks]);

  const handleNewTask = useCallback(() => {
    // startCreateFlow is store-only; if the URL is on an overlay, the next
    // openArtifact to the same overlay would no-op. Reset the URL first.
    if (isOverlayPath(location.pathname)) {
      closeOverlay();
    }
    startCreateTaskFlow();
  }, [closeOverlay, location.pathname, startCreateTaskFlow]);

  return (
    <ArtifactListPanelContent
      onClose={onClose}
      search={{
        placeholder: t('search.placeholder'),
        value: search,
        onChange: setSearch,
      }}
      filters={{
        hasActiveFilters,
        label: t('filters.title'),
        pills: <TaskFilterPills />,
        panel: <TaskFilters />,
      }}
      header={{
        title: t('tasks.title'),
        count: totalCount,
        addLabel: t('tasks.addTask'),
        onAdd: handleNewTask,
        addDisabled: isAddTaskDisabled,
      }}
    >
      <TasksPanelList
        tasks={allFilteredTasks}
        loading={isLoading}
        hasLoaded={hasLoaded}
        error={tasksError}
        onRetry={fetchTasks}
        hasNextPage={hasNextPage}
        onLoadMore={loadMoreTasks}
      />
    </ArtifactListPanelContent>
  );
});
